// 📄 especificacionTecnicaService.ts
// "Especificaciones Técnicas" (ver EspecificacionesTecnicasSection.tsx,
// ítem en Funcionalidades): documentos de especificación (ej. Word con el
// detalle de un desarrollo/mapeo) que se van completando entre el área
// Funcional (la inicia) y Técnica (agrega su parte) — a diferencia de
// Casos de Prueba, acá CUALQUIER miembro del equipo puede editar en
// cualquier momento (es justamente un trabajo colaborativo entre áreas,
// restringir a "solo el creador" rompería el flujo). Mismo patrón de
// permisos que badisZ/transaccionesZ: cliente solo lee.
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDocs,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { uploadAttachments, UploadedAttachment } from './storageService.ts';

export type EspecificacionEstado = 'Pendiente' | 'Finalizado' | 'Cancelado';
export type EspecificacionPrioridad = 'Alta' | 'Media' | 'Baja';
export type AreaResponsable = 'Funcional' | 'Técnica';

export const ESPECIFICACION_ESTADOS: EspecificacionEstado[] = ['Pendiente', 'Finalizado', 'Cancelado'];
export const ESPECIFICACION_PRIORIDADES: EspecificacionPrioridad[] = ['Alta', 'Media', 'Baja'];
export const AREAS_RESPONSABLES: AreaResponsable[] = ['Funcional', 'Técnica'];

export interface EspecificacionFormData {
  titulo: string;
  descripcion: string;
  solucionRelacionada: string;
  // Igual criterio que CasoPruebaFormData: no todas las soluciones tienen
  // un catálogo de canales definido, quedan sin canal específico.
  canalId?: string;
  canalNombre?: string;
  prioridad: EspecificacionPrioridad;
  // De quién es el turno de trabajar en la especificación ahora mismo —
  // arranca en 'Funcional' (quien la inicia) y se va pasando a 'Técnica'
  // a medida que cada área complete su parte.
  areaResponsable: AreaResponsable;
}

export interface EspecificacionTecnica extends EspecificacionFormData {
  id: string;
  estado: EspecificacionEstado;
  adjuntos: UploadedAttachment[];
  creadoPorUid: string;
  creadoPorNombre: string;
  fechaCreacion?: any;
}

const especificacionesRef = (projectId: string) => collection(db, "projects", projectId, "especificacionesTecnicas");
const especificacionRef = (projectId: string, id: string) => doc(db, "projects", projectId, "especificacionesTecnicas", id);

export const getEspecificaciones = async (projectId: string): Promise<EspecificacionTecnica[]> => {
  const snap = await getDocs(especificacionesRef(projectId));
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<EspecificacionTecnica, 'id'>) }))
    .sort((a, b) => (b.fechaCreacion?.toMillis?.() ?? 0) - (a.fechaCreacion?.toMillis?.() ?? 0));
};

export const createEspecificacion = async (
  projectId: string,
  data: EspecificacionFormData,
  files: File[],
  creadoPorUid: string,
  creadoPorNombre: string
): Promise<string> => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");

  const ref = doc(especificacionesRef(projectId));
  const adjuntos = files.length > 0
    ? await uploadAttachments(`projects/${projectId}/especificacionesTecnicas/${ref.id}`, files)
    : [];

  const payload: Record<string, any> = {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    solucionRelacionada: data.solucionRelacionada,
    prioridad: data.prioridad,
    areaResponsable: data.areaResponsable,
    estado: 'Pendiente' as EspecificacionEstado,
    creadoPorUid,
    creadoPorNombre,
    fechaCreacion: serverTimestamp(),
    adjuntos
  };
  if (data.canalId) {
    payload.canalId = data.canalId;
    payload.canalNombre = data.canalNombre || '';
  }

  await setDoc(ref, payload);
  return ref.id;
};

export interface EspecificacionDatosEditables {
  titulo: string;
  descripcion: string;
  prioridad: EspecificacionPrioridad;
  areaResponsable: AreaResponsable;
  // Igual patrón que CasoPruebaDatosEditables: los que el usuario decidió
  // CONSERVAR, se combinan con los recién subidos.
  adjuntosExistentes: UploadedAttachment[];
}

export const updateEspecificacionDatos = async (
  projectId: string,
  id: string,
  datos: EspecificacionDatosEditables,
  nuevosArchivos: File[]
) => {
  if (!datos.titulo.trim()) throw new Error("El título es obligatorio.");
  const nuevosAdjuntos = nuevosArchivos.length > 0
    ? await uploadAttachments(`projects/${projectId}/especificacionesTecnicas/${id}`, nuevosArchivos)
    : [];
  await updateDoc(especificacionRef(projectId, id), {
    titulo: datos.titulo.trim(),
    descripcion: datos.descripcion.trim(),
    prioridad: datos.prioridad,
    areaResponsable: datos.areaResponsable,
    adjuntos: [...datos.adjuntosExistentes, ...nuevosAdjuntos]
  });
};

export const updateEspecificacionEstado = async (projectId: string, id: string, estado: EspecificacionEstado) => {
  await updateDoc(especificacionRef(projectId, id), { estado });
};
